import { useMemo, useState } from 'react';

import { Center, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import { THealthDomain, THealthDomainScores } from '@/types/Health.types';
import DrawnCirclesBackground from '@/components/atoms/DrawnCirclesBackground';
import {
  NO_OF_WELLNESS_DOMAINS,
  OVERALL_HEALTH_DOMAIN,
  WELLNESS_DOMAINS_DISPLAY_LABELS,
  WELLNESS_DOMAINS_DISPLAY_LABELS_REVERSE,
  WELLNESS_STATE_LIMIT_OK,
} from '@/constants';
import { OUTER_CIRCLE_SIZE } from '@/constants/dimensions';
import colors from '@/theme/colors';
import CircleFillOutWithText from './CircleFillOutWithText';
import WellnessStateCircleChunk from './WellnessStateCircleChunk';
import { getColorFromPercentage } from '@/utils/getColorFromPercentage';
import { domainNameToSvgId } from '@/utils/stringUtils';
import ColoredDotsDescription from '../ColoredDotsDescription';

const AVATAR_SIZE = 180;
const CIRCLE_DIAMETER_PADDING = 20;

const ID_COMBINED_WELLNESS_SCORE = 'combined-wellness-score';

const MARGIN_FROM_CENTER_CIRCLE_SIDE_MIDDLE_ITEMS = '490px';
const MARGIN_FROM_CENTER_CIRCLE_SIDE_BOTTOM_ITEMS = '370px';

// Helper function to safely get wellness data
const safeGetWellnessData = (data: { domainName: string; value: number }[], index: number) => {
  if (index < 0 || index >= data.length) {
    // Return default value when index is out of bounds
    return { domainName: 'Unknown', value: 0 };
  }
  return data[index];
};

const SORTER_WELLNESS_DOMAINS_INDEX_ARRAY = [
  {
    index: 0,
    top: 45,
    left: MARGIN_FROM_CENTER_CIRCLE_SIDE_BOTTOM_ITEMS,
    right: undefined,
  },
  {
    index: 6,
    top: 45,
    left: undefined,
    right: MARGIN_FROM_CENTER_CIRCLE_SIDE_BOTTOM_ITEMS,
  },
  {
    index: 1,
    top: OUTER_CIRCLE_SIZE / 3.8,
    left: MARGIN_FROM_CENTER_CIRCLE_SIDE_MIDDLE_ITEMS,
    right: undefined,
  },
  {
    index: 5,
    top: OUTER_CIRCLE_SIZE / 3.8,
    left: undefined,
    right: MARGIN_FROM_CENTER_CIRCLE_SIDE_MIDDLE_ITEMS,
  },
  {
    index: 2,
    top: OUTER_CIRCLE_SIZE / 2.1,
    left: MARGIN_FROM_CENTER_CIRCLE_SIDE_MIDDLE_ITEMS,
    right: undefined,
  },
  {
    index: 4,
    top: OUTER_CIRCLE_SIZE / 2.1,
    left: undefined,
    right: MARGIN_FROM_CENTER_CIRCLE_SIDE_MIDDLE_ITEMS,
  },
  {
    index: 3,
    top: OUTER_CIRCLE_SIZE - 260,
    left: undefined,
    right: undefined,
  },
];

const WellnessStateCircle = ({
  wellnessData,
  totalScore,
  showCenteredTextOverlay,
  avatarTooltipJSX = null,
  greenScoreDescription = 'Your Strengths',
  yellowScoreDescription = 'Developing Potential',
  redScoreDescription = 'Opportunities for Growth',
  selectedDomain,
  setSelectedDomain,
  hoveredDomain,
  setHoveredDomain,
  hoveredDomainContent,
}: {
  wellnessData: THealthDomainScores;
  totalScore?: number;
  showCenteredTextOverlay?: boolean;
  avatarTooltipJSX?: React.JSX.Element | null;
  greenScoreDescription?: string;
  yellowScoreDescription?: string;
  redScoreDescription?: string;
  selectedDomain?: THealthDomain;
  setSelectedDomain?: (domain: THealthDomain | undefined) => void;
  hoveredDomain?: THealthDomain;
  setHoveredDomain?: (domain: THealthDomain | undefined) => void;
  hoveredDomainContent?: React.JSX.Element;
}) => {
  const totalScoreX10 = totalScore ? totalScore * 10 : null;

  const updateSelectedDomain = (domainLabel: string) => {
    if (selectedDomain && domainLabel === WELLNESS_DOMAINS_DISPLAY_LABELS[selectedDomain]) {
      setSelectedDomain?.(undefined);
    } else {
      setSelectedDomain?.(WELLNESS_DOMAINS_DISPLAY_LABELS_REVERSE[domainLabel]);
    }
  };

  const areDomainsSelectable = !!setSelectedDomain;
  const areDomainsHoverable = !!setHoveredDomain;

  const wellnessDataSorted = useMemo(() => {
    const wellnessDataArray = Object.entries(wellnessData).map(([wellnessDomain, wellnessValue]) => ({
      domainName: WELLNESS_DOMAINS_DISPLAY_LABELS[wellnessDomain as THealthDomain],
      value: wellnessValue,
    }));

    wellnessDataArray.sort((a, b) => (a.value > b.value ? 1 : -1));
    return wellnessDataArray;
  }, [wellnessData]);

  const [avatarTooltipPosition, setAvatarTooltipPosition] = useState<{ x: number; y: number } | null>(null);

  const combinedWellnessScore = Math.floor(
    Object.values(wellnessData).reduce((acc, next) => (acc += next), 0) / NO_OF_WELLNESS_DOMAINS,
  );

  return (
    <Center width={'100%'} position={'relative'} flexDir={'column'}>
      <DrawnCirclesBackground>
        <Center position={'absolute'} width={'100%'} height={'100%'}>
          <Flex
            position={'absolute'}
            borderRadius={'50%'}
            zIndex={2}
            backgroundColor={'white'}
            width={'30%'}
            height={'30%'}
            alignItems={'center'}
            justifyContent={'center'}
            boxShadow="0px 12.5px 25px 0px #00543B1A"
            cursor={areDomainsSelectable ? 'pointer' : undefined}
            onClick={areDomainsSelectable ? () => setSelectedDomain(OVERALL_HEALTH_DOMAIN as THealthDomain) : undefined}
          >
            {avatarTooltipPosition && (
              <VStack
                position={'absolute'}
                left={`${avatarTooltipPosition.x}px`}
                top={`${avatarTooltipPosition.y}px`}
                backgroundColor={'white'}
                borderRadius={'8px'}
                boxShadow={'0px 6px 34px 0px #00417933'}
                justifyContent={'flex-start'}
                padding={'20px'}
                zIndex={4}
              >
                {avatarTooltipJSX}
              </VStack>
            )}
            {/* Mouse tracker for Avatar tooltip position */}
            <Flex
              width={'100%'}
              height={'100%'}
              position={'absolute'}
              zIndex={2}
              onMouseMove={
                avatarTooltipJSX
                  ? ({ nativeEvent }) => {
                      setAvatarTooltipPosition({
                        x:
                          nativeEvent.offsetX < AVATAR_SIZE / 2
                            ? -AVATAR_SIZE * 1.5 - CIRCLE_DIAMETER_PADDING + nativeEvent.offsetX / 2
                            : AVATAR_SIZE - CIRCLE_DIAMETER_PADDING + nativeEvent.offsetX / 2,
                        y: nativeEvent.offsetY,
                      });
                    }
                  : undefined
              }
              onMouseOut={avatarTooltipJSX ? () => setAvatarTooltipPosition(null) : undefined}
            />

            <CircleFillOutWithText
              circleDiameter={
                showCenteredTextOverlay ? AVATAR_SIZE / 2.25 : AVATAR_SIZE / 2.25 + CIRCLE_DIAMETER_PADDING
              }
              boxShadow={showCenteredTextOverlay ? undefined : ''}
              svgId={ID_COMBINED_WELLNESS_SCORE}
              thickness={6}
              percentageFilled={totalScoreX10 ?? combinedWellnessScore}
              percentageTextStyle={{
                display: showCenteredTextOverlay ? 'initial' : 'none',
                color: colors.text.darkBlue,
                fontWeight: 600,
                fontSize: 20,
              }}
              circleColor={getColorFromPercentage(totalScoreX10 ?? combinedWellnessScore)}
              backgroundTransparent
            />
          </Flex>
        </Center>

        {/* ALL-COVERING MIST... */}
        <Center
          display={selectedDomain ? 'flex' : 'none'}
          opacity={selectedDomain ? 0.9 : 0}
          width={'1000px'}
          marginRight={'-150px'}
          marginLeft={'-150px'}
          marginTop={'50px'}
          height={'120%'}
          bgGradient={`radial-gradient(${colors.background.lightBlue}, ${colors.background.lightBlue}CC, ${colors.background.lightBlue}AA, transparent)`}
          zIndex={selectedDomain ? 2 : 1}
          onClick={() => setSelectedDomain?.(undefined)}
        />

        {SORTER_WELLNESS_DOMAINS_INDEX_ARRAY.map((sortedInfo, i) => {
          const safelyRetrievedData = safeGetWellnessData(wellnessDataSorted, sortedInfo.index);

          const isSelected =
            selectedDomain && safelyRetrievedData.domainName === WELLNESS_DOMAINS_DISPLAY_LABELS[selectedDomain];

          const isHovered =
            hoveredDomain && safelyRetrievedData.domainName === WELLNESS_DOMAINS_DISPLAY_LABELS[hoveredDomain];

          return (
            <Flex
              key={i}
              justifyContent={i === 0 ? 'center' : undefined}
              position={'absolute'}
              top={`${sortedInfo.top - (isSelected ? 20 : isHovered ? 10 : 0) + (safelyRetrievedData.value >= WELLNESS_STATE_LIMIT_OK ? 4 : 0)}px`}
              left={sortedInfo.left}
              right={sortedInfo.right}
              cursor={areDomainsSelectable ? 'pointer' : undefined}
              onMouseEnter={() =>
                areDomainsHoverable
                  ? setHoveredDomain(
                      WELLNESS_DOMAINS_DISPLAY_LABELS_REVERSE[wellnessDataSorted[sortedInfo.index].domainName],
                    )
                  : undefined
              }
              onMouseLeave={() => setHoveredDomain?.(undefined)}
              onClick={
                areDomainsSelectable
                  ? () => updateSelectedDomain(wellnessDataSorted[sortedInfo.index].domainName)
                  : undefined
              }
              transition={'top 1s'}
              zIndex={isSelected || isHovered ? 4 : 1}
            >
              <HStack zIndex={3}>
                <WellnessStateCircleChunk
                  title={safelyRetrievedData.domainName}
                  svgId={domainNameToSvgId(safelyRetrievedData.domainName)}
                  percentageFilled={safelyRetrievedData.value}
                  reversed={i % 2 === 1}
                />
              </HStack>

              {/* Shadow Under "Lifted" Domain */}
              <HStack
                position={'absolute'}
                top={'55px'}
                left={'50px'}
                boxShadow={'0px 30px 34px 14px #00001234'}
                borderRadius={'50%'}
                width={'50%'}
                background={'transparent'}
                opacity={isSelected || isHovered ? 1 : 0}
                transition={'opacity 2s'}
                zIndex={2}
              ></HStack>

              {/* Hovered Domain Content */}
              {isHovered && (
                <VStack
                  position={'absolute'}
                  top={'55px'}
                  // left={
                  //   i === 1 || i === 3 || i === 5
                  //     ? hoveredDomainContent
                  //       ? '-190px'
                  //       : '-120px'
                  //     : hoveredDomainContent
                  //       ? '125px'
                  //       : '50px'
                  // }
                  left={hoveredDomainContent ? '125px' : '50px'}
                  width={'300px'}
                  zIndex={4}
                >
                  {hoveredDomainContent ?? (
                    <VStack
                      backgroundColor={'white'}
                      padding={'20px'}
                      borderRadius={'8px'}
                      boxShadow="0px 6.97px 34.86px 0px #00417930"
                    >
                      <Text variant={'urbanistBold'} color={'text.mediumBlue'}>
                        {'No Data for this domain'}
                      </Text>
                    </VStack>
                  )}
                </VStack>
              )}
            </Flex>
          );
        })}
      </DrawnCirclesBackground>

      {/* Green, Yellow and Red Score Descriptions */}
      <ColoredDotsDescription
        dots={[
          { color: 'extra.teal', description: greenScoreDescription },
          { color: 'extra.yellowStrong', description: yellowScoreDescription },
          { color: 'extra.red', description: redScoreDescription },
        ]}
      />
    </Center>
  );
};

export default WellnessStateCircle;
